import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Editor from '@monaco-editor/react';
import SearchBar from './SearchBar';
import { registerDataTableSuggestEditor } from './DataTableMustacheSuggestions';

const LINE_HEIGHT = 20;
const PADDING = 24;

interface MonacoEditorWithSearchProps {
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  language?: string;
  height?: string | number;
  autoHeight?: boolean;
  error?: string | null;
  placeholder?: string;
  readOnly?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Key to force remount (e.g. from parent) */
  editorKey?: string;
  /** When provided, shows copy button before the search bar; called on copy click */
  onCopy?: () => void;
  /** Tooltip for copy button when copied (e.g. "Copied!") */
  copyCopied?: boolean;
  /** 'overlay' = search in editor toolbar (default). 'header' = search in external header slot */
  searchPosition?: 'overlay' | 'header';
  /** When searchPosition='header', render SearchBar into this container (top-right of section header) */
  searchContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** When true, suppress Monaco JSON schema/value completions (e.g. $schema popup after `:""`). */
  disableCompletions?: boolean;
  /** When true, suppress Monaco JSON schema errors for $function(...) placeholders in request bodies. */
  allowFunctionPlaceholders?: boolean;
  /** Called when the Monaco editor instance is mounted (e.g. for imperative table→body sync). */
  onEditorMount?: (editor: any) => void;
}

/**
 * Monaco Editor with floating search bar overlay.
 * Highlights matches and supports Next/Prev, Enter/Shift+Enter.
 */
const MonacoEditorWithSearch: React.FC<MonacoEditorWithSearchProps> = ({
  value,
  defaultValue,
  onChange,
  language = 'json',
  height = 240,
  autoHeight = false,
  error,
  placeholder = '',
  readOnly = false,
  onFocus,
  onBlur,
  editorKey,
  onCopy,
  copyCopied = false,
  searchPosition = 'overlay',
  searchContainerRef,
  onEditorMount,
  disableCompletions = false,
  allowFunctionPlaceholders = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [useRegex, setUseRegex] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matches, setMatches] = useState<any[]>([]);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const decorationIdsRef = useRef<string[]>([]);

  const contentForHeight = value ?? defaultValue ?? '';
  const hasContent = (contentForHeight || '').trim().length > 0;

  const computedHeight =
    !autoHeight ? height : Math.min(Math.max((contentForHeight || '').split(/\r?\n/).length * LINE_HEIGHT + PADDING, 80), typeof height === 'number' ? height : 400);
  const editorHeight = autoHeight ? computedHeight : height;
  const useUncontrolled = defaultValue !== undefined;
  const matchCount = matches.length;

  const updateDecorations = useCallback(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || matchCount === 0) {
      decorationIdsRef.current = editor?.deltaDecorations(decorationIdsRef.current, []) ?? [];
      return;
    }
    const model = editor.getModel();
    if (!model) return;
    const decos: any[] = matches.map((m, i) => {
      const r = m.range;
      const range = monaco
        ? new monaco.Range(r.startLineNumber, r.startColumn, r.endLineNumber, r.endColumn)
        : { startLineNumber: r.startLineNumber, startColumn: r.startColumn, endLineNumber: r.endLineNumber, endColumn: r.endColumn };
      const className = i === currentMatchIndex ? 'search-highlight-match-active' : 'search-highlight-match';
      return {
        range,
        options: {
          inlineClassName: className,
          backgroundColor: i === currentMatchIndex ? '#ff9800' : '#ffeb3b',
        },
      };
    });
    decorationIdsRef.current = editor.deltaDecorations(decorationIdsRef.current, decos);
  }, [matches, currentMatchIndex, matchCount]);

  const findMatches = useCallback(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco || !searchQuery.trim()) {
      setMatches([]);
      return;
    }
    const model = editor.getModel();
    if (!model) return;
    try {
      const found = model.findMatches(
        searchQuery,
        false,
        useRegex,
        !caseInsensitive,
        null,
        false
      );
      setMatches(found);
      setCurrentMatchIndex((prev) => (found.length > 0 ? Math.min(prev, found.length - 1) : 0));
    } catch {
      setMatches([]);
    }
  }, [searchQuery, caseInsensitive, useRegex]);

  useEffect(() => {
    findMatches();
  }, [searchQuery, caseInsensitive, useRegex, findMatches]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;
    const disposable = model.onDidChangeContent(() => findMatches());
    return () => disposable.dispose();
  }, [findMatches]);

  useEffect(() => {
    updateDecorations();
  }, [updateDecorations]);

  const handleNext = useCallback(() => {
    setCurrentMatchIndex((prev) => (prev + 1) % Math.max(1, matchCount));
  }, [matchCount]);

  const handlePrev = useCallback(() => {
    setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % Math.max(1, matchCount));
  }, [matchCount]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || matchCount === 0) return;
    const m = matches[currentMatchIndex];
    if (m) {
      editor.setSelection(m.range);
      editor.revealRangeInCenter(m.range);
    }
    updateDecorations();
  }, [currentMatchIndex, matchCount, matches, updateDecorations]);

  const copyButton = onCopy ? (
    <Tooltip title={copyCopied ? 'Copied!' : 'Copy request body'}>
      <IconButton
        size="small"
        onClick={onCopy}
        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
        aria-label="Copy request body"
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const searchBarEl = (
    <SearchBar
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      caseInsensitive={caseInsensitive}
      onCaseInsensitiveChange={setCaseInsensitive}
      useRegex={useRegex}
      onUseRegexChange={setUseRegex}
      matchCount={matchCount}
      currentMatchIndex={currentMatchIndex}
      onNext={handleNext}
      onPrev={handlePrev}
      compact
      overlay={true}
      inOverlayGroup={true}
      prefix={searchPosition === 'header' ? copyButton : undefined}
    />
  );

  useEffect(() => {
    if (!hasContent && searchQuery) setSearchQuery('');
  }, [hasContent, searchQuery]);

  // Header slot lives in a sibling (e.g. ResponseMetaBar). Use createPortal from THIS React tree —
  // do not use createRoot on that node: a nested root fights the parent on unmount (removeChild errors).
  const [headerPortalContainer, setHeaderPortalContainer] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (searchPosition !== 'header' || !searchContainerRef) {
      setHeaderPortalContainer(null);
      return;
    }
    const el = searchContainerRef.current;
    if (el) {
      setHeaderPortalContainer(el);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      const n = searchContainerRef.current;
      setHeaderPortalContainer(n);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
      setHeaderPortalContainer(null);
    };
  }, [searchPosition, searchContainerRef, editorKey]);

  const showOverlay = searchPosition === 'overlay';
  const overlayContent = showOverlay && (
    <Box
      sx={{
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        p: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        pointerEvents: 'none',
      }}
    >
      {onCopy && (
        <Tooltip title={copyCopied ? 'Copied!' : 'Copy request body'}>
          <IconButton
            sx={{ pointerEvents: 'auto', '&:hover': { bgcolor: 'action.hover' } }}
            size="small"
            onClick={onCopy}
            aria-label="Copy request body"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ pointerEvents: 'auto' }}>{searchBarEl}</Box>
    </Box>
  );

  const headerPortal =
    searchPosition === 'header' && headerPortalContainer
      ? createPortal(searchBarEl, headerPortalContainer)
      : null;

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {headerPortal}
      {overlayContent}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Editor
          key={editorKey ?? 'monaco-search'}
          height={editorHeight}
          language={language}
          {...(useUncontrolled ? { defaultValue } : { value: value ?? '' })}
          onChange={(v: string | undefined) => onChange(v ?? '')}
          onMount={(editor, monaco) => {
            editorRef.current = editor;
            monacoRef.current = monaco;
            const unregister = registerDataTableSuggestEditor(editor);
            const contentSub = editor.onDidChangeModelContent(() => {
              document.dispatchEvent(new Event('dt-suggest-refresh'));
            });
            const cursorSub = editor.onDidChangeCursorPosition(() => {
              document.dispatchEvent(new Event('dt-suggest-refresh'));
            });
            editor.onDidDispose(() => {
              contentSub.dispose();
              cursorSub.dispose();
              unregister();
            });
            if (allowFunctionPlaceholders && language === 'json') {
              monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                validate: false,
                enableSchemaRequest: false,
              });
            }
            onEditorMount?.(editor);
            onFocus && editor.onDidFocusEditorWidget(onFocus);
            onBlur && editor.onDidBlurEditorWidget(onBlur);
            findMatches();
          }}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            fontFamily: 'Fira Mono, monospace',
            scrollBeyondLastLine: false,
            wordWrap: 'off',
            formatOnPaste: !readOnly,
            formatOnType: false,
            lineNumbers: 'on',
            renderLineHighlight: 'all',
            tabSize: 2,
            readOnly: readOnly,
            domReadOnly: readOnly,
            renderWhitespace: 'none',
            quickSuggestions: disableCompletions
              ? false
              : { other: true, comments: false, strings: true },
            suggestOnTriggerCharacters: !disableCompletions,
            wordBasedSuggestions: disableCompletions ? 'off' : 'currentDocument',
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'auto',
              verticalScrollbarSize: 0,
              horizontalScrollbarSize: 17,
              useShadows: false,
              alwaysConsumeMouseWheel: false,
            },
            automaticLayout: true,
            fixedOverflowWidgets: true,
          }}
          theme="vs"
        />
      </Box>
    </Box>
  );
};

export default MonacoEditorWithSearch;
